import { useState } from 'react';
import { Page } from '../components/Page';
import { Alert, Button, cardClass, ErrorNotice, Loading } from '../components/ui';
import { ApiError, apiSend, type CliSession } from '../lib/api';
import { formatDate, timeAgo } from '../lib/format';
import { useApi } from '../lib/use-api';

/** Computers where the MOGZO CLI is signed in, and signing them out. */
export function CliSessions() {
  const [version, setVersion] = useState(0);
  const result = useApi<{ sessions: CliSession[] }>('/v1/dev/cli/sessions', version);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(key: string, path: string) {
    setBusy(key);
    setError(null);
    try {
      await apiSend('POST', path);
      setVersion((n) => n + 1);
    } catch (failed) {
      setError(failed instanceof ApiError ? failed.message : 'Could not reach the API. Try again.');
    } finally {
      setBusy(null);
    }
  }

  const sessions = result.status === 'ready' ? result.data.sessions : [];

  return (
    <Page
      title="CLI sessions"
      description="Computers where the MOGZO CLI is signed in to your account. Each stays signed in for 90 days after it was last used."
      actions={
        sessions.length > 1 && (
          <Button
            variant="danger"
            disabled={busy !== null}
            onClick={() => run('all', '/v1/dev/cli/sessions/revoke-all')}
          >
            {busy === 'all' ? 'Signing out…' : 'Sign out everywhere'}
          </Button>
        )
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      {result.status === 'loading' && <Loading />}
      {result.status === 'error' && <ErrorNotice error={result.error} />}
      {result.status === 'ready' && (
        <div className={`${cardClass} overflow-x-auto`}>
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-[13px] text-muted">
                <th scope="col" className="px-5 py-3.5 font-semibold">
                  Computer
                </th>
                <th scope="col" className="px-3 py-3.5 font-semibold">
                  Signed in
                </th>
                <th scope="col" className="px-3 py-3.5 font-semibold">
                  Last used
                </th>
                <th scope="col" className="px-3 py-3.5 font-semibold">
                  From
                </th>
                <th scope="col" className="px-5 py-3.5 font-semibold">
                  <span className="sr-only">Sign out</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((session) => (
                <tr key={session.id} className="border-t border-divider">
                  <td className="px-5 py-3.5">
                    <span className="block font-semibold">{session.deviceName}</span>
                    <span className="block text-muted">{session.deviceSystem}</span>
                  </td>
                  <td className="px-3 py-3.5">{formatDate(session.createdAt)}</td>
                  <td
                    className="px-3 py-3.5"
                    title={`Expires ${formatDate(session.expiresAt)} if unused`}
                  >
                    {timeAgo(session.lastUsedAt)}
                  </td>
                  <td className="px-3 py-3.5 font-mono text-[13px]">{session.lastIp ?? '—'}</td>
                  <td className="px-5 py-3.5 text-right">
                    <Button
                      variant="secondary"
                      disabled={busy !== null}
                      aria-label={`Sign out ${session.deviceName}`}
                      onClick={() => run(session.id, `/v1/dev/cli/sessions/${session.id}/revoke`)}
                    >
                      {busy === session.id ? 'Signing out…' : 'Sign out'}
                    </Button>
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr className="border-t border-divider">
                  <td colSpan={5} className="px-5 py-7 text-center text-muted">
                    No computers are signed in. When the MOGZO CLI is available, run{' '}
                    <span className="font-mono text-ink">mogzo login</span> to sign one in.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-sm leading-relaxed text-muted">
        Lost a laptop? Sign it out here. Changing your password on mogzo.com signs out every CLI
        session too.
      </p>
    </Page>
  );
}
